// Imports for external libraries go here.
import React, { FC, useRef, useEffect, useState, useMemo, memo } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { RichText, Icon } from '@marriott/mi-ui-library';
import { MButton, MButtonType, MButtonSize, MButtonIconPlacement } from '@marriott/mi-web-ui-kit';
import { MTag, MTagType } from '@marriott/mi-web-ui-kit';
import { DataCellProps, TableRowProps, CardData, CardType } from './DataCellComponent.types';
import { StyledDataCell } from './DataCellComponent.styles';
import { DataType, IconType, getPricingContentForUser } from '../../modules/utils';
import { CobrandImage } from '../CobrandImage/CobrandImage';
import { ARIA_LABELS, StringBoolean } from '../../modules/constants/constants';

/**
 * AEM component configuration for DataCell
 */
export const DataCellConfig = {
  emptyLabel: 'DataCell',
  isEmpty: () => true,
  resourceType: `/components/content/datacell`,
};

/**
 * DataCellComp - Core DataCell component
 *
 * Renders individual cells in card comparison tables with support for:
 * - Multiple data types (header, icons, empty fields)
 * - Card type badges (personal/business/none)
 * - Elite status indicators
 * - Author mode metadata display
 * - Dynamic pricing terms based on authentication
 *
 * Performance optimizations:
 * - Memoized component to prevent unnecessary re-renders
 * - useMemo for computed values
 * - Sub-components extracted for better code splitting
 */
export const DataCellComp: FC<DataCellProps> = memo<DataCellProps>((props: DataCellProps) => {
  const {
    cardType,
    styleclass,
    categoryIconPath,
    imagePath,
    altText,
    isAuthorMode,
    categoryId,
    groupid,
    eliteStatus,
    primaryCTAText,
    primaryCtaURL,
    openInNewTabPrimary,
    dataType,
    iconType,
    componentId,
    eliteStatusColor,
    trackingProperties,
    pricingTermsSignedIn = '',
    pricingTermsGuest = '',
    addSpaceBelowImage,
    verticalImage,
    isEnhancedComparison,
  } = props;

  const ref = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const isVerticalImageAlignment = addSpaceBelowImage || verticalImage;
  const [pricingContent, setPricingContent] = useState('');
  useEffect(() => {
    setPricingContent(getPricingContentForUser(pricingTermsGuest, pricingTermsSignedIn) ?? '');
  }, [pricingTermsGuest, pricingTermsSignedIn]);

  // Memoized computed values for performance
  const headerText = useMemo(
    () => props.cardSummary || props.categoryHeading,
    [props.cardSummary, props.categoryHeading]
  );

  const isHeader = useMemo(() => props.type === DataType.Header, [props.type]);

  return (
    <StyledDataCell
      className={clsx(styleclass)}
      id={componentId}
      data-component-name="m-cobrand-DataCellComponent"
      data-testid="cobrand-DataCellComponent"
    >
      <div
        className={clsx(
          'd-flex flex-column cell-wrapper',
          isAuthorMode && 'cell-border mb-2',
          isEnhancedComparison ? 'enhanced-comparison-cell' : ''
        )}
      >
        {/* Elite Status Badge - only show in header cells */}
        {isHeader && eliteStatus && !isEnhancedComparison && (
          <div className={clsx('py-3 px-4 badge', `${eliteStatusColor}-status`)}>
            <RichText componentId="elite-status" customClass="t-font-inverse-s" text={eliteStatus} />
          </div>
        )}

        {/* Card Type Badge - only show in header cells */}
        {cardType && isHeader && (
          <>
            {isEnhancedComparison ? (
              <div className={clsx(`${eliteStatusColor}-status px-4 pt-4`)}>
                <div className="d-flex justify-content-space-between pb-2 mb-1">
                  {cardType === 'none' ? (
                    <div className={clsx('pt-lg-3 mt-lg-2 pb-lg-1')} />
                  ) : (
                    <MTag
                      data-component-name="m-cobrand-DataCellComponent-badge"
                      data-testid="cobrand-DataCellComponent-badge"
                      aLabel={{ text: cardType }}
                      className={clsx(
                        cardType === 'personal' && 'bg-personal',
                        cardType === 'business' && 'bg-business',
                        'm-badge-inline t-overline-normal pt-1 px-2'
                      )}
                      type={MTagType.Inverse}
                    />
                  )}
                </div>
                <CobrandImage
                  defaultImageURL={imagePath}
                  altText={altText}
                  customClass={clsx(
                    verticalImage ? 'vertical-card-image' : 'card-image',
                    addSpaceBelowImage ? 'pb-2 mb-5' : 'pr-3'
                  )}
                />
              </div>
            ) : (
              <div className="d-flex justify-content-end">
                {cardType === 'none' ? (
                  <div className={clsx('pt-lg-3 mt-lg-2 pb-lg-1')} />
                ) : (
                  <span
                    data-component-name="m-cobrand-DataCellComponent-badge"
                    data-testid="cobrand-DataCellComponent-badge"
                    className={clsx(
                      cardType === 'personal' && 'bg-personal',
                      cardType === 'business' && 'bg-business',
                      'm-badge-inline t-overline-normal p-2'
                    )}
                  >
                    {cardType}
                  </span>
                )}
              </div>
            )}
          </>
        )}

        {/* Main Content Area */}
        <div className={clsx('p-3', props.categoryHeading ? 'justify-content-flex-start' : 'd-flex flex-grow-1')}>
          {/* Author Mode View */}
          {isAuthorMode && categoryId && (
            <div>
              {categoryId && <div> Category ID: {categoryId}</div>}
              {groupid && <div> Group ID: {groupid}</div>}
              {imagePath && headerText && (
                <div className="d-flex flex-column flex-md-row">
                  <CobrandImage
                    defaultImageURL={imagePath}
                    altText={altText}
                    customClass={clsx('card-image', addSpaceBelowImage ? 'pb-2 mb-5' : 'pr-3')}
                  />
                  <RichText componentId="data-cell" text={headerText} />
                </div>
              )}
              {!imagePath && headerText && (
                <div className="d-flex">
                  {categoryIconPath && (
                    <div className={clsx('mr-2')}>
                      <Icon iconClass={`${categoryIconPath} icon-l`} />
                    </div>
                  )}
                  <RichText componentId="data-cell" text={headerText} />
                </div>
              )}
            </div>
          )}

          {/* Standard View - Cell Content */}
          {!isAuthorMode && (
            <div className={clsx(headerText && 'd-flex flex-grow-1')}>
              {isHeader && headerText && !isEnhancedComparison && (
                <div className="d-flex flex-column justify-content-between flex-grow-1">
                  <div className={clsx('d-flex flex-column', !isVerticalImageAlignment && 'flex-lg-row')}>
                    <CobrandImage
                      defaultImageURL={imagePath}
                      altText={altText}
                      customClass={clsx(
                        verticalImage ? 'vertical-card-image' : 'card-image',
                        addSpaceBelowImage ? 'pb-2 mb-5' : 'pr-3'
                      )}
                    />
                    <RichText
                      customClass={clsx('mt-3', !isVerticalImageAlignment && 'mt-lg-0')}
                      componentId="data-cell"
                      text={headerText}
                    />
                  </div>
                  {pricingContent && (
                    <RichText text={pricingContent} componentId="pricing-terms" customClass="my-3 tertiary-links" />
                  )}
                  {primaryCTAText && (
                    <div>
                      <MButton
                        id={`${componentId}-btn`}
                        buttonText={primaryCTAText}
                        ctaLink={primaryCtaURL}
                        buttonType={MButtonType.Primary}
                        size={MButtonSize.M}
                        openInANewTab={openInNewTabPrimary === true || openInNewTabPrimary === StringBoolean.TRUE}
                        openInANewTabText={
                          openInNewTabPrimary === true || openInNewTabPrimary === StringBoolean.TRUE
                            ? ARIA_LABELS.OPEN_NEW_TAB
                            : undefined
                        }
                        iconPlacement={
                          openInNewTabPrimary === true || openInNewTabPrimary === StringBoolean.TRUE
                            ? MButtonIconPlacement.Trailing
                            : MButtonIconPlacement.None
                        }
                        trackingProperties={trackingProperties}
                        ref={ref as React.Ref<HTMLButtonElement>}
                      />
                    </div>
                  )}
                </div>
              )}
              {isHeader && headerText && isEnhancedComparison && (
                <div className="d-flex flex-column flex-grow-1">
                  <div className={clsx('d-flex flex-column', !isVerticalImageAlignment && 'flex-lg-row')}>
                    <RichText
                      customClass={clsx('mt-3', !isVerticalImageAlignment && 'mt-lg-0', 'header-text-container')}
                      componentId="data-cell"
                      text={headerText}
                    />
                  </div>
                  {pricingContent && (
                    <RichText
                      text={pricingContent}
                      componentId="pricing-terms"
                      customClass="my-3 tertiary-links flex-grow-1"
                    />
                  )}
                  {primaryCTAText && (
                    <div>
                      <MButton
                        id={`${componentId}-btn`}
                        buttonText={primaryCTAText}
                        ctaLink={primaryCtaURL}
                        buttonType={MButtonType.Primary}
                        size={MButtonSize.M}
                        openInANewTab={openInNewTabPrimary === true || openInNewTabPrimary === StringBoolean.TRUE}
                        openInANewTabText={
                          openInNewTabPrimary === true || openInNewTabPrimary === StringBoolean.TRUE
                            ? ARIA_LABELS.OPEN_NEW_TAB
                            : undefined
                        }
                        iconPlacement={
                          openInNewTabPrimary === true || openInNewTabPrimary === StringBoolean.TRUE
                            ? MButtonIconPlacement.Trailing
                            : MButtonIconPlacement.None
                        }
                        trackingProperties={trackingProperties}
                        ref={ref as React.Ref<HTMLButtonElement>}
                      />
                    </div>
                  )}
                </div>
              )}
              {!isHeader && headerText && dataType !== DataType.Status && (
                <div className="d-flex">
                  {categoryIconPath && (
                    <div className={clsx('mr-2')}>
                      <Icon iconClass={`${categoryIconPath} icon-l`} />
                    </div>
                  )}
                  <RichText componentId="data-cell" text={headerText} />
                </div>
              )}
            </div>
          )}

          {/* Icon Cell */}
          {dataType === DataType.Icons && iconType && iconType !== 'none' && (
            <div
              className={clsx(`icon-check-${IconType[iconType as keyof typeof IconType]}`, 'icon-wrapper text-center')}
            >
              <Icon iconClass="icon-check icon-inverse" />
            </div>
          )}
          {/* Status Cell */}
          {dataType === DataType.Status && eliteStatusColor && headerText && eliteStatusColor !== 'none' && (
            <div className={clsx('text-center')}>
              <MTag
                data-component-name="m-cobrand-DataCellComponent-badge"
                data-testid="cobrand-DataCellComponent-badge"
                aLabel={{ text: headerText }}
                className={clsx(`${eliteStatusColor}-status-cell`, 'm-badge-inline')}
                type={MTagType.Inverse}
              />
            </div>
          )}

          {/* Empty Cell Placeholder */}
          {dataType === DataType.Emtpy && <div className="text-left"> ___</div>}
        </div>
      </div>
    </StyledDataCell>
  );
});

DataCellComp.displayName = 'DataCellComp';

/**
 * DataCell - AEM Editable Wrapper
 *
 * Wraps DataCellComp with AEM EditableComponent for content authoring
 */
export const DataCell: FC<DataCellProps> = (props: DataCellProps) => {
  return (
    <EditableComponent config={DataCellConfig} {...props}>
      <DataCellComp {...props} />
    </EditableComponent>
  );
};

/**
 * Utility function to calculate column class based on number of cards
 * @param cardCount - Number of cards in the comparison
 * @returns Bootstrap column class
 */
const getColumnClass = (cardCount: number): string => {
  return cardCount === 3 ? 'col-4' : 'col-6';
};

/**
 * TableRow Component
 *
 * Renders a row in the card comparison table with:
 * - Category cell on the left (1/4 or 1/3 width)
 * - Data cells on the right (responsive column layout)
 *
 * Performance optimizations:
 * - Memoized component to prevent unnecessary re-renders
 * - Column class calculation extracted to utility function
 */
export const TableRow: FC<TableRowProps> = memo<TableRowProps>((props: TableRowProps) => {
  const { category, dataList, type, cardIdFromURL, customClass, columnCount, slotControls, isEnhancedComparison } =
    props;

  const columnClass = useMemo(
    () => getColumnClass(columnCount ?? cardIdFromURL.length),
    [cardIdFromURL.length, columnCount]
  );
  const hasSlotControls = Boolean(slotControls?.length);

  return (
    <div className={clsx('d-flex flex-wrap flex-column flex-md-row data-row col-12 px-0')}>
      <DataCell
        {...category}
        styleclass={clsx(customClass, 'data-cell category-cell col-12 col-md-4 col-lg-3 px-0')}
        groupid={category.groupId}
        isEnhancedComparison={isEnhancedComparison}
      />
      <div className="d-flex col-12 col-md-8 col-lg-9 p-0 data-section">
        {dataList.map((data: CardData, index: number) =>
          hasSlotControls ? (
            <div
              key={`${data.cardId}-${index}`}
              className={clsx(
                'comparison-slot-wrapper d-flex flex-column position-relative p-0',
                !data.cardId && 'comparison-slot-wrapper--empty',
                columnClass
              )}
            >
              {slotControls?.[index]}
              <DataCell
                {...data}
                cardType={data.cardType as CardType}
                styleclass={clsx('data-cell flex-grow-1 px-0 h-100')}
                type={type}
                groupid={category.groupId}
                isEnhancedComparison={isEnhancedComparison}
              />
            </div>
          ) : (
            <DataCell
              key={`${data.cardId}-${index}`}
              {...data}
              cardType={data.cardType as CardType}
              styleclass={clsx('data-cell px-0', isEnhancedComparison ?? 'd-flex flex-grow-1', columnClass)}
              type={type}
              groupid={category.groupId}
            />
          )
        )}
      </div>
    </div>
  );
});

TableRow.displayName = 'TableRow';
